
import { useState, useEffect } from 'react';
import { useAppraisalData } from './useAppraisalData';
import { useAppraisalActions } from './useAppraisalActions';

export const useAppraisalState = (
  personalGoals: any[],
  selectedMemberGoals: any[],
  activeTab: string,
  teamMembers: any[],
  userIsSupervisor: boolean
) => {
  const [goalSelfRatings, setGoalSelfRatings] = useState<{[key: string]: string}>({});
  const [goalManagerRatings, setGoalManagerRatings] = useState<{[key: string]: string}>({});
  const [goalComments, setGoalComments] = useState<{[key: string]: string}>({});
  const [goalManagerComments, setGoalManagerComments] = useState<{[key: string]: string}>({});
  const [savedGoals, setSavedGoals] = useState<Set<string>>(new Set());
  const [savedManagerGoals, setSavedManagerGoals] = useState<Set<string>>(new Set());
  const [goalAppraisals, setGoalAppraisals] = useState<{[key: string]: any}>({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editManagerDialogOpen, setEditManagerDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<any>(null);

  const { 
    currentUser, 
    supervisorName, 
    appraisalMetrics, 
    loadGoalAppraisals, 
    calculateAppraisalMetrics,
    refreshTrigger,
    triggerRefresh
  } = useAppraisalData(personalGoals, selectedMemberGoals, activeTab, teamMembers);

  const appraisalState = {
    goalSelfRatings,
    setGoalSelfRatings,
    goalManagerRatings,
    setGoalManagerRatings,
    goalComments,
    setGoalComments,
    goalManagerComments,
    setGoalManagerComments,
    savedGoals,
    setSavedGoals,
    savedManagerGoals,
    setSavedManagerGoals,
    goalAppraisals,
    setGoalAppraisals,
    editDialogOpen,
    setEditDialogOpen,
    editManagerDialogOpen,
    setEditManagerDialogOpen,
    selectedGoal,
    setSelectedGoal
  };

  const actions = useAppraisalActions(
    appraisalState,
    userIsSupervisor,
    teamMembers,
    calculateAppraisalMetrics,
    triggerRefresh
  );

  // Load appraisal data when component mounts or when refresh is triggered
  useEffect(() => {
    const loadData = async () => {
      try {
        const { 
          appraisalMap, 
          savedGoalIds, 
          savedManagerGoalIds, 
          ratingsMap, 
          managerRatingsMap, 
          commentsMap, 
          managerCommentsMap 
        } = await loadGoalAppraisals();
        
        setGoalAppraisals(appraisalMap);
        setSavedGoals(new Set<string>(Array.from(savedGoalIds).filter((id): id is string => typeof id === 'string')));
        setSavedManagerGoals(new Set<string>(Array.from(savedManagerGoalIds).filter((id): id is string => typeof id === 'string')));
        setGoalSelfRatings(ratingsMap);
        setGoalManagerRatings(managerRatingsMap);
        setGoalComments(commentsMap);
        setGoalManagerComments(managerCommentsMap);
      } catch (error) {
        console.error("Error loading appraisal data:", error);
      }
    };

    loadData();
  }, [personalGoals, selectedMemberGoals, activeTab, refreshTrigger]);

  return {
    // State
    goalSelfRatings,
    goalManagerRatings,
    goalComments,
    goalManagerComments,
    savedGoals,
    savedManagerGoals,
    goalAppraisals,
    editDialogOpen,
    editManagerDialogOpen,
    selectedGoal,
    currentUser,
    supervisorName,
    appraisalMetrics,
    
    // State setters
    setGoalSelfRatings,
    setGoalManagerRatings,
    setGoalComments,
    setGoalManagerComments,
    setSavedGoals,
    setSavedManagerGoals,
    setGoalAppraisals,
    setEditDialogOpen,
    setEditManagerDialogOpen,
    setSelectedGoal,
    
    // Actions
    ...actions,
    
    // Other functions
    calculateAppraisalMetrics
  };
};
